import React from 'react';
import { motion } from 'framer-motion';
import { Building, Scale, Plane, ArrowRight, CheckCircle } from 'lucide-react';
import { PageHeader } from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';
export function LegalExpat() {
  const sections = [{
    title: 'Company Establishment',
    icon: <Building size={32} data-id="element-360" />,
    desc: 'Seamless setup of corporate entities in Indonesia, tailored to your investment strategy.',
    items: ['PT PMA (Foreign Direct Investment Company)', 'Local PT (Limited Liability Company)', 'CV (Commanditaire Vennootschap)', 'OSS (Online Single Submission) Registration', 'NIB (Business Identification Number)', 'Business License & KBLI Selection', 'PBG (Building Approval) & SLF (Certificate of Function Worthiness)']
  }, {
    title: 'Legal & Corporate Documents',
    icon: <Scale size={32} data-id="element-361" />,
    desc: 'Protecting your business interests with robust legal frameworks and compliance strategies.',
    items: ['Corporate Document Drafting & Review', 'Commercial Contracts & Agreements', 'Legal Compliance Audits', 'Corporate Governance Advisory', 'Employment Contracts & Labor Law', 'Intellectual Property Registration', 'Legal Consultation & Dispute Resolution']
  }, {
    title: 'Visa & Expat Services',
    icon: <Plane size={32} data-id="element-362" />,
    desc: 'Comprehensive immigration solutions for investors, directors, and foreign workers.',
    items: ['Investor KITAS (1 & 2 Years)', 'Working KITAS & Work Permits (RPTKA)', 'Dependent KITAS for Family', 'Multiple Entry Business Visa', 'Visa Extensions & Renewals', 'Company Sponsorship Services', 'Immigration Consultation']
  }];
  return <div className="bg-navy-900 min-h-screen" data-id="element-363">
      <PageHeader title="Legal, Licensing & Expat Services" subtitle="Expert navigation of Indonesian corporate law, business licensing, and immigration requirements for a secure foundation." backgroundImage="https://images.unsplash.com/photo-1589829085413-56de8ae18c73?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80" data-id="element-364" />

      <section className="py-24" data-id="element-365">
        <div className="max-w-content mx-auto px-6 md:px-12" data-id="element-366">
          <div className="text-center max-w-3xl mx-auto mb-20" data-id="element-367">
            <h2 className="font-display text-4xl font-bold text-white mb-6" data-id="element-368">
              Secure Your Business Foundation
            </h2>
            <p className="font-body text-gray-400 text-lg leading-relaxed" data-id="element-369">
              Establishing and operating a business in Indonesia involves
              complex regulatory landscapes. Our legal and licensing experts
              provide clear, compliant, and efficient pathways for foreign and
              local investors to establish their presence in Bali and across
              Indonesia.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-370">
            {sections.map((section, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.2,
            duration: 0.6
          }} className="bg-navy-800 p-10 rounded-card border border-navy-700 hover:border-gold/30 transition-colors flex flex-col" data-id="element-371">
                <div className="w-16 h-16 bg-navy-700 rounded-2xl flex items-center justify-center text-gold mb-6" data-id="element-372">
                  {section.icon}
                </div>
                <h3 className="font-heading text-2xl font-semibold text-white mb-4" data-id="element-373">
                  {section.title}
                </h3>
                <p className="font-body text-gray-400 text-sm mb-8" data-id="element-374">
                  {section.desc}
                </p>
                <ul className="space-y-4 flex-grow" data-id="element-375">
                  {section.items.map((item, i) => <li key={i} className="flex items-start gap-3 text-gray-300 font-body text-sm" data-id="element-376">
                      <CheckCircle size={18} className="text-gold shrink-0 mt-0.5" data-id="element-377" />
                      <span data-id="element-378">{item}</span>
                    </li>)}
                </ul>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Timeline Process */}
      <section className="py-24 bg-navy-800 border-y border-navy-700" data-id="element-379">
        <div className="max-w-content mx-auto px-6 md:px-12" data-id="element-380">
          <div className="text-center mb-16" data-id="element-381">
            <h2 className="font-display text-4xl font-bold text-white mb-4" data-id="element-382">
              PT PMA Establishment Process
            </h2>
            <p className="font-body text-gray-400 text-lg max-w-2xl mx-auto" data-id="element-383">
              A streamlined approach to setting up your Foreign Direct
              Investment company.
            </p>
          </div>

          <div className="max-w-4xl mx-auto relative" data-id="element-384">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-navy-700 md:-translate-x-1/2" data-id="element-385"></div>

            {[{
            step: 1,
            title: 'Initial Consultation',
            desc: 'Determining business classification (KBLI), investment plan, and corporate structure.'
          }, {
            step: 2,
            title: 'Deed of Establishment',
            desc: 'Drafting and signing the Article of Association before a Public Notary.'
          }, {
            step: 3,
            title: 'Ministry Approval',
            desc: 'Obtaining approval from the Ministry of Law and Human Rights (SK Kemenkumham).'
          }, {
            step: 4,
            title: 'Tax Registration',
            desc: 'Acquiring the company Tax Identification Number (NPWP).'
          }, {
            step: 5,
            title: 'Business Licensing',
            desc: 'Registering via OSS to obtain NIB and standard business licenses.'
          }].map((item, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1,
            duration: 0.5
          }} className={`relative flex items-center justify-between mb-12 last:mb-0 ${index % 2 === 0 ? 'md:flex-row-reverse' : ''}`} data-id="element-386">
                <div className="hidden md:block w-5/12" data-id="element-387"></div>

                <div className="absolute left-0 md:left-1/2 w-8 h-8 rounded-full bg-navy-900 border-2 border-gold md:-translate-x-1/2 flex items-center justify-center z-10" data-id="element-388">
                  <span className="text-gold text-xs font-bold" data-id="element-389">
                    {item.step}
                  </span>
                </div>

                <div className="w-full pl-12 md:pl-0 md:w-5/12" data-id="element-390">
                  <div className="bg-navy-900 p-6 rounded-card border border-navy-700" data-id="element-391">
                    <h4 className="font-heading text-xl font-semibold text-white mb-2" data-id="element-392">
                      {item.title}
                    </h4>
                    <p className="font-body text-sm text-gray-400" data-id="element-393">
                      {item.desc}
                    </p>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      <section className="py-24 text-center" data-id="element-394">
        <div className="max-w-3xl mx-auto px-6 md:px-12" data-id="element-395">
          <h2 className="font-display text-4xl font-bold text-white mb-6" data-id="element-396">
            Start Your Business Journey
          </h2>
          <p className="font-body text-gray-400 text-lg mb-10" data-id="element-397">
            Schedule a consultation with our legal experts to discuss your
            company establishment or visa requirements.
          </p>
          <Button to="/contact" icon={<ArrowRight size={18} data-id="element-399" />} data-id="element-398">
            Consult with Legal Expert
          </Button>
        </div>
      </section>
    </div>;
}